import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { pickBy, head } from 'lodash';

import { aclHelper, usePersistState } from 'helper';

import { useCreateModal } from 'partial/components/Modal';
import Button from 'partial/components/Button';
import { Plus } from '@phosphor-icons/react';
import List from 'partial/components/List';
import SearchBar from 'partial/components/input/SearchBar';
import Pagination from 'partial/components/Pagination';
import ThumbIcon from 'partial/components/ThumbIcon';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import RoleDetails from './container/RoleDetails';
import AddUpdateRole from './modals/AddUpdateRole';
import { useRoles } from './hooks';

const INITIAL_STATE = {
  q: '',
  status: { label: '', value: '' },
};

const RolesAndPermissions = () => {
  const { setSubLabel } = useModuleContext();

  const navigate = useNavigate();
  const createModal = useCreateModal();
  const [filter, setFilter] = useState(INITIAL_STATE);
  const [selected, setSelected] = usePersistState('role-id', '', {
    keepOnUnmount: false,
  });

  const [isLoading, roles, meta] = useRoles(
    pickBy({ ...filter, status: filter?.status?.value }),
  );

  const id = head(roles)?.id;

  const onSubSelect = (item) => {
    setSelected(item?.id);
    navigate(`${item?.id}`);
  };

  const handleAddRole = useCallback(() => {
    createModal({
      content: (onClose) => <AddUpdateRole onClose={onClose} />,
    });
  }, [createModal]);

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} role${meta?.total === 1 ? '' : 's'}`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  return (
    <section className="flex bg-white border border-neutral-200 rounded-md flex-grow overflow-hidden">
      <div className="flex flex-col border-r border-neutral-200 w-[280px] flex-shrink-0">
        <div className="p-3 border-b border-neutral-200">
          <SearchBar
            name="q"
            value={filter?.q}
            onChange={setFilter}
            inputClassName="!w-full"
            containerClassName="!w-full"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <List
            data={roles}
            isLoading={isLoading}
            selectedId={selected}
            selectedKey="id"
            onSelectRow={onSubSelect}
            containerClassName="!w-full"
            render={(item) => {
              const name = item?.name;
              return (
                <div className="flex items-center gap-3 px-4 py-3 w-full min-w-0">
                  <ThumbIcon className="h-9 w-9 rounded-full" alt={name} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium capitalize truncate">
                      {name}
                    </span>
                    <span className="text-xs text-neutral-500 lowercase truncate">
                      {item?.code}
                    </span>
                  </div>
                </div>
              );
            }}
            emptyGlyph="results"
            emptyStatement="No roles match this search."
          />
        </div>
        <div className="p-3 border-t border-neutral-200 bg-neutral-50 space-y-3">
          {aclHelper(
            'role',
            'w',
            <Button
              primary
              icon={Plus}
              className="w-full"
              onClick={handleAddRole}
            >
              New role
            </Button>,
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-500 tabular-nums">
              {roles?.length || 0} of {meta?.total || 0}
            </p>
            <Pagination onChange={setFilter} value={meta} withPage={false} />
          </div>
        </div>
      </div>

      <Routes>
        <Route path=":id/*" element={<RoleDetails roles={roles} />} />
        {roles?.length > 0 && (
          <Route
            index
            element={<Navigate replace to={`${head(roles)?.id}`} />}
          />
        )}
      </Routes>
    </section>
  );
};

export default RolesAndPermissions;
